import {
    Box,
    FormControl,
    InputLabel,
    Select,
    TextField,
    Typography
} from "@mui/material";
import React,
{ forwardRef } from "react";

import { MeasurementUnit } from "../../../API";
import { useIssues } from "../contexts/IssuesContextProvider";
import { useState } from "react";

export type ProcureDimensionsContentProps = {
    issueId: string;
    ref?: React.RefObject<HTMLFormElement>;
    onSubmit?: (formData: FormData) => Promise<void>;
    onClose?: () => void;
}

export const ProcureDimensionsContent = forwardRef<HTMLFormElement, ProcureDimensionsContentProps>(
    ({ issueId, onSubmit, onClose }, ref) => {
        const { parentIdToDimensionMap, issueIdToIssueMap } = useIssues();

        const issue = issueIdToIssueMap.get(issueId);
        if (!issue) {
            return null;
        }
        const existingDimension = parentIdToDimensionMap.get(issueId);

        const [isError, setIsError] = useState(false);

        const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
            if (!onSubmit) {
                return;
            }
            event.preventDefault();

            const formData = new FormData(event.currentTarget);

            try {
                await onSubmit(formData);
            } catch (error) {
                console.error("Error submitting dimensions:", error);
                setIsError(true);
            } finally {
                onClose?.();
            }
        };

        return (
            <>
                {!isError && (
                    <form ref={ref} onSubmit={handleSubmit}>
                        <Box
                            display="flex"
                            justifyContent="start"
                            alignItems="start"
                            width="100%"
                            flexDirection="column"
                            gap={2}
                        >
                            <Box>
                                <Typography variant="h6" fontWeight="bold">
                                    Dimensions
                                </Typography>
                                <Typography variant="body2">
                                    Dimensions are not required, but they help us provide more accurate cost estimates
                                </Typography>
                            </Box>
                            <Box display="flex" flexDirection="row" gap={2} flexWrap="wrap">
                                <TextField
                                    type="number"
                                    id="length"
                                    name="length"
                                    label="Length"
                                    variant="outlined"
                                    size="small"
                                    sx={{ maxWidth: "100px", backgroundColor: "white" }}
                                    defaultValue={existingDimension?.measurement?.length ?? null}
                                    inputProps={{ min: 0 }}
                                />
                                <TextField
                                    type="number"
                                    id="width"
                                    name="width"
                                    label="Width"
                                    variant="outlined"
                                    size="small"
                                    sx={{ maxWidth: "100px", backgroundColor: "white" }}
                                    inputProps={{ min: 0 }}
                                    defaultValue={existingDimension?.measurement?.width ?? null}
                                />
                                <FormControl size="small" sx={{ maxWidth: 100, backgroundColor: "white" }}>
                                    <InputLabel>Unit</InputLabel>
                                    <Select
                                        id="unit"
                                        name="unit"
                                        native
                                        defaultValue={existingDimension?.measurement?.measurementUnit ?? MeasurementUnit.FOOT}
                                        label="Unit"
                                    >
                                        {Object.values(MeasurementUnit).map((unitValue) => (
                                            <option key={unitValue} value={unitValue}>
                                                {unitValue}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>
                    </form>
                )}
                {
                    isError && (
                        <Typography variant="body1" align="center" color="error">
                            There was an error submitting your request. Please try again.
                        </Typography>
                    )
                }
            </>
        );
    }
);
